// ScheduleList.tsx
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../components/page-header/page-header";
import useToast from "../../../hooks/useToast";
import { TContext, TFilterModel, TPrivilege } from "../../../types/config";
import {
  eMONTHMAPPING,
  ePrivileges,
  eREGIONS,
  eREPORTTYPE,
  eResultCode,
  eSCHEDULETYPE,
  eWEEKDAYS,
  monthMapping,
  reportTypeMapping,
  scheduleTypeMapping,
  weekDaysMapping,
  eDATE_FORMAT,
} from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { dateOnlyFormat, defaultFilterParams } from "../../../utils/constants";
import {
  AddEditReportSchedules,
  GetReportSchedulesList,
} from "../../../utils/api.constant";
import { TReport, TSchedule } from "../../../types";
import DateUtils from "../../../utils/date";
import ColumnManager from "../../../components/configurable-columns";
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import CustomDrawer from "../../../components/drawer";
import { UserContext } from "../../../context";
import ScheduleReportForm from "./schedule-form";
import utils from "../../../utils";
import dayjs from "dayjs";
import GenericPopover from "../../../components/pop-over";
import ActionCard from "../../../components/actionCard/actionCard";
import CustomModal from "../../../components/modal";
import Utils from "../../../utils";

export default function ScheduleList() {
  // initialization of hooks
  const { post } = useFetch(); // custom hook used to call the apis
  const [isLoading, setIsLoading] = useState(true); // to manage the loader
  const [data, setData] = useState<TSchedule[]>([]); // to store config group data
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams); // object to fetch the data in list
  const { onShowToast } = useToast(); // custom hook used to show toast
  const context: TContext = useContext(UserContext);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<TSchedule | null>(
    null,
  );
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const showDeleteModal = (schedule: TSchedule) => {
    setSelectedSchedule(schedule);
    setIsDeleteModalVisible(true);
  };
  const showStatusUpdateModal = (schedule: TSchedule) => {
    setSelectedSchedule(schedule);
    setIsStatusModalVisible(true);
  };

  const initialColumns: any[] = [
    {
      title: "Report Type",
      dataIndex: "reportType",
      key: "reportType",
      // width: "20%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.reportType).localeCompare(String(b.reportType)),
      render: (reportType: number) => {
        return reportTypeMapping[reportType as eREPORTTYPE] || reportType;
      },
      filters: Object.keys(reportTypeMapping).map((key) => ({
        text: reportTypeMapping[key as unknown as eREPORTTYPE],
        value: key,
      })),
      onFilter: (value: string | number | boolean, record: TReport) =>
        record.reportType === Number(value),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      // width: "20%",
      filterSearch: true,
      sorter: (a: TSchedule, b: TSchedule) =>
        String(a.customerName).localeCompare(String(b.customerName)),
      filters: utils.getUniqueFilters(data, "customerName"),
      onFilter: (value: string | number | boolean, record: TSchedule) =>
        record.customerName.includes(value as string),
    },
    {
      title: "Schedule Type",
      dataIndex: "reportScheduleType",
      key: "reportScheduleType",
      // width: "20%",
      filterSearch: true,
      sorter: (a: TSchedule, b: TSchedule) =>
        String(a.reportScheduleType).localeCompare(
          String(b.reportScheduleType),
        ),
      render: (scheduleType: number) => {
        return (
          <Tag
            color={
              scheduleType === eSCHEDULETYPE.DAILY
                ? "#0063BF"
                : scheduleType === eSCHEDULETYPE.WEEKLY
                  ? "#660E1D"
                  : scheduleType === eSCHEDULETYPE.MONTHLY
                    ? "#C44B0A"
                    : "#8196A6"
            }
          >
            {scheduleTypeMapping[scheduleType as eSCHEDULETYPE] || scheduleType}
          </Tag>
        );
      },
      filters: Object.keys(scheduleTypeMapping).map((key) => ({
        text: scheduleTypeMapping[key as unknown as eSCHEDULETYPE],
        value: key,
      })),
      onFilter: (value: string | number | boolean, record: TSchedule) =>
        record.reportScheduleType === Number(value),
    },
    {
      title: "Scheduled Time",
      dataIndex: "scheduleTime",
      key: "scheduleTime",
      // width: "20%",
      render: (scheduleTime: string) => {
        return scheduleTime ? dayjs(scheduleTime)?.format("HH:mm:ss") : "-";
      },
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      // width: "15%",
      render: (day: number, schedule: TSchedule) => {
        return schedule.reportScheduleType === eSCHEDULETYPE.WEEKLY
          ? weekDaysMapping[day as eWEEKDAYS]
          : schedule.reportScheduleType === eSCHEDULETYPE.MONTHLY
            ? monthMapping[day as eMONTHMAPPING]
            : schedule.reportScheduleType === eSCHEDULETYPE.ANNUAL
              ? DateUtils.format(schedule?.reportDate, dateOnlyFormat)
              : "N/A";
      },
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: "15%",
      filterSearch: true,
      sorter: (a: TReport, b: TReport) =>
        String(a.createdOn).localeCompare(String(b.createdOn)),
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, context?.user?.dateFormat);
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      filters: [
        { text: "Active", value: 1 },
        { text: "In Active", value: 0 },
      ],
      // width: "10%",
      filterSearch: true,
      sorter: (a: TSchedule, b: TSchedule) =>
        (a.isActive?.toString() || "").localeCompare(
          b.isActive?.toString() || "",
        ), // Handle undefined case
      onFilter: (value: string | number | boolean, record: TSchedule) =>
        record.isActive?.toString() === value,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "var(--bs-primary)" : "#f50"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      // width: "7%",
      render: (schedule: TSchedule) => (
        <>
          {Utils.isUserHasAccess(
            context?.user?.privilegeList as TPrivilege[],
            ePrivileges.DELETESCHEDULE,
          ) ? (
            <GenericPopover
              placement="left"
              content={
                <div className="standardActionBox">
                  <>
                    {Utils.isUserHasAccess(
                      context?.user?.privilegeList as TPrivilege[],
                      ePrivileges.EDITSCHEDULE,
                    ) ? (
                      <CustomDrawer
                        trigger={
                          <ActionCard text="Edit">
                            <EditOutlined />
                          </ActionCard>
                        }
                        component={ScheduleReportForm} // Passing DealerForm component
                        componentProps={{
                          onRefreshList: () => fetchData(),
                          schedule,
                        }}
                        title="Schedule Report"
                        size="default"
                      />
                    ) : null}
                  </>
                  {Utils.isUserHasAccess(
                    context?.user?.privilegeList as TPrivilege[],
                    ePrivileges.DELETESCHEDULE,
                  ) ? (
                    <ActionCard
                      text="Delete"
                      onClick={() => showDeleteModal(schedule)}
                    >
                      <DeleteOutlined />
                    </ActionCard>
                  ) : null}

                  {Utils.isUserHasAccess(
                    context?.user?.privilegeList as TPrivilege[],
                    ePrivileges.DELETESCHEDULE,
                  ) ? (
                    <ActionCard
                      text={schedule?.isActive ? "Disable" : "Enable"}
                      onClick={() => showStatusUpdateModal(schedule)}
                    >
                      {!schedule?.isActive ? (
                        <CheckCircleOutlined style={{ fontSize: 18 }} />
                      ) : (
                        <CloseCircleOutlined style={{ fontSize: 18 }} />
                      )}
                    </ActionCard>
                  ) : null}
                </div>
              }
              icon={
                <MoreOutlined
                  style={{
                    fontSize: "20px",
                    cursor: "pointer",
                    display:
                      Utils.isUserHasAccess(
                        context?.user?.privilegeList as TPrivilege[],
                        ePrivileges.DELETESCHEDULE,
                      ) ||
                      Utils.isUserHasAccess(
                        context?.user?.privilegeList as TPrivilege[],
                        ePrivileges.EDITSCHEDULE,
                      )
                        ? "visible"
                        : "none",
                  }}
                />
              }
            />
          ) : null}
        </>
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns);

  useEffect(() => {
    fetchData();
  }, []);

  const updateColumnFilters = (dataset: TSchedule[]) => {
    const reportTypeFilters = Object.entries(reportTypeMapping)
      .filter(([key]) =>
        dataset.some((item) => item.reportType === Number(key)),
      )
      .map(([key, value]) => ({
        text: value,
        value: key,
      }));
    const customerNameFilters = Array.from(
      new Set(dataset.map((item) => item.customerName)),
    ).map((name) => ({ text: name, value: name }));
    const scheduleTypeFilters = Object.entries(scheduleTypeMapping)
      .filter(([key]) =>
        dataset.some((item) => item.reportScheduleType === Number(key)),
      )
      .map(([key, value]) => ({
        text: value,
        value: key,
      }));
    const statusFilters = [
      { text: "Active", value: 1 },
      { text: "In Active", value: 0 },
    ].filter((option) =>
      dataset.some((item) => Number(item.isActive) === option.value),
    );
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "reportType") {
          return { ...col, filters: reportTypeFilters };
        }
        if (col.key === "customerName") {
          return { ...col, filters: customerNameFilters };
        }
        if (col.key === "reportScheduleType") {
          return { ...col, filters: scheduleTypeFilters };
        }
        if (col.key === "isActive") {
          return { ...col, filters: statusFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase()),
    );
    const reportTypeMatch =
      !activeFilters.reportType ||
      activeFilters.reportType.includes(String(item.reportType));

    const customerNameMatch =
      !activeFilters.customerName ||
      activeFilters.customerName.includes(item.customerName);

    const scheduleTypeMatch =
      !activeFilters.reportScheduleType ||
      activeFilters.reportScheduleType.includes(
        String(item.reportScheduleType),
      );

    const statusMatch =
      !activeFilters.isActive ||
      activeFilters.isActive.includes(Number(item.isActive));

    return (
      searchMatch &&
      reportTypeMatch &&
      customerNameMatch &&
      scheduleTypeMatch &&
      statusMatch
    );
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetReportSchedulesList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TSchedule, index: number) => ({
          key: (item?.id ?? "").toString(),
          srNo: index + 1,
          ...item,
        }));
        setData(formattedData);
        setFilterParams({
          ...(filterModel as TFilterModel),
          totalRows: data.length,
        });
        updateColumnFilters(formattedData);
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const mapToExportRow = (item: TSchedule) => {
    const { reportType, reportScheduleType, day, isActive, ...restData } =
      item as any;

    const formatExportDate = (dateVal: any, showTime: boolean = false) => {
      return DateUtils.formatForExport(
        dateVal,
        showTime,
        context?.user?.dateFormat as eDATE_FORMAT,
      );
    };

    return {
      reportType:
        reportTypeMapping[item.reportType as eREPORTTYPE] || item.reportType,
      reportScheduleType:
        scheduleTypeMapping[item.reportScheduleType as eSCHEDULETYPE] ||
        item.reportScheduleType,
      day:
        item.reportScheduleType === eSCHEDULETYPE.WEEKLY
          ? weekDaysMapping[day as eWEEKDAYS]
          : item.reportScheduleType === eSCHEDULETYPE.MONTHLY
            ? monthMapping[day as eMONTHMAPPING]
            : item.reportScheduleType === eSCHEDULETYPE.ANNUAL
              ? formatExportDate(item?.reportDate)
              : "N/A",
      isActive: item.isActive ? "Active" : "Inactive",
      createdOn: formatExportDate(item.createdOn),
      updatedOn: formatExportDate(item.updatedOn),
      ...restData,
    };
  };

  const exportData = data.map(mapToExportRow);
  const exportFilteredData = filteredData.map(mapToExportRow);

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  const deleteSchedule = async () => {
    try {
      const payload = {
        data: {
          id: selectedSchedule?.id,
          isDeleted: 1,
          customerId: selectedSchedule?.customerId,
        },
      };
      const response = await post(AddEditReportSchedules, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const changeScheduleStatus = async () => {
    try {
      const { isActive, customerId, ...restSchedules } =
        selectedSchedule as TSchedule;
      const payload = {
        data: {
          id: selectedSchedule?.id,
          isActive: selectedSchedule?.isActive ? 0 : 1,
          customerId: selectedSchedule?.customerId,
          ...restSchedules,
        },
      };
      const response = await post(AddEditReportSchedules, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteOk = async () => {
    if (selectedSchedule) {
      await deleteSchedule();
    }
    setSelectedSchedule(null);
    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setSelectedSchedule(null);
    setIsDeleteModalVisible(false);
  };

  const handleStatusUpdateOk = async () => {
    if (selectedSchedule) {
      await changeScheduleStatus();
    }
    setSelectedSchedule(null);
    setIsStatusModalVisible(false);
  };

  const handleStatusUpdateCancel = () => {
    setSelectedSchedule(null);
    setIsStatusModalVisible(false);
  };

  return (
    <div className="pageBody">
      <PageHeader
        // icon={<FaObjectGroup />}
        title={`${filterParams?.totalRows} Total Schedules`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {Utils.isUserHasAccess(
            context?.user?.privilegeList as TPrivilege[],
            ePrivileges.CREATESCHEDULE,
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Create
                </Button>
              }
              component={ScheduleReportForm} // Passing DealerForm component
              componentProps={{
                onRefreshList: () => fetchData(),
              }}
              title="Schedule Report"
              size="default"
            />
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            filteredData={exportFilteredData}
            allColumns={initialColumns}
            visibleColumns={visibleColumns}
            moduleName="Schedule List"
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content",
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Confirm Delete"
        isOpen={isDeleteModalVisible}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          You are about to delete report schedule for{" "}
          <b>{selectedSchedule?.customerName}</b>?
        </p>
      </CustomModal>
      <CustomModal
        title="Confirm Status Update"
        isOpen={isStatusModalVisible}
        onOk={handleStatusUpdateOk}
        onCancel={() => handleStatusUpdateCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          You are about to {selectedSchedule?.isActive ? "disable" : "enable"}{" "}
          report schedule for <b>{selectedSchedule?.customerName}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
